/**
 * File: LineOfBusinessWelcomePage.tsx
 * Description: One-time onboarding page that asks the user to confirm their
 *   persona and, when needed, line of business before entering Archie.
 * Author: GitHub Copilot
 * Created: 2026-05-28
 * Last Modified: 2026-05-28
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import headerBg from '../../assets/header-bg.svg';
import logo from '../../assets/logo.svg';
import { LoadingSpinner } from '../../components/LoadingSpinner/LoadingSpinner';
import {
  LINE_OF_BUSINESS_LABEL,
  LINE_OF_BUSINESS_OPTIONS_ARIA_LABEL,
  PERSONA_LABEL,
  PERSONA_OPTION_ITEMS,
  PERSONA_OPTIONS,
  PERSONA_OPTIONS_ARIA_LABEL,
  isLineOfBusinessRequired,
} from '../../constants/persona';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { UserProfileService } from '../../services/userProfileService';

import './LineOfBusinessWelcomePage.css';

const EMPTY_VALUE = '';
const HERO_TITLE = 'AI Assisted Architecture';
const WELCOME_TITLE = 'Welcome to Archie';
const WELCOME_DESCRIPTION =
  'Please select your persona before continuing. If you choose Architect, also select your line of business. You can always update both from your profile page.';
const LINE_OF_BUSINESS_OPTIONS_ERROR_MESSAGE =
  'We could not load the available line of business options. Please try again.';
const SAVE_ERROR_MESSAGE =
  'We could not save your profile selections. Please try again.';
const LINE_OF_BUSINESS_OPTIONS_LOADING_LABEL =
  'Loading line of business options...';
const SAVE_BUTTON_LABEL = 'Confirm and Continue';
const SAVING_BUTTON_LABEL = 'Saving...';
const RETRY_OPTIONS_BUTTON_LABEL = 'Try Again';

export const LineOfBusinessWelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, setProfile } = useUserProfile();
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string>(
    profile?.persona ?? PERSONA_OPTIONS[0],
  );
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<string>(
    profile?.line_of_business ?? EMPTY_VALUE,
  );
  const [optionsRequestKey, setOptionsRequestKey] = useState<number>(0);
  const [lineOfBusinessLoading, setLineOfBusinessLoading] =
    useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [lineOfBusinessOptionsError, setLineOfBusinessOptionsError] =
    useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const shouldShowLineOfBusiness = isLineOfBusinessRequired(selectedPersona);
  const isConfirmDisabled =
    !selectedPersona ||
    (shouldShowLineOfBusiness &&
      (!selectedLineOfBusiness || lineOfBusinessLoading)) ||
    saving;

  useEffect(() => {
    setSelectedPersona(profile?.persona ?? PERSONA_OPTIONS[0]);
    setSelectedLineOfBusiness(profile?.line_of_business ?? EMPTY_VALUE);
  }, [profile?.persona, profile?.line_of_business]);

  useEffect(() => {
    if (!shouldShowLineOfBusiness) {
      setLineOfBusinessOptionsError(null);
      return;
    }

    let isDisposed = false;

    const loadOptions = async (): Promise<void> => {
      setLineOfBusinessLoading(true);
      setLineOfBusinessOptionsError(null);
      try {
        const availableOptions = await UserProfileService.getLineOfBusinessOptions();
        if (isDisposed) {
          return;
        }
        setLineOfBusinessOptions(availableOptions);
        setSelectedLineOfBusiness((currentValue) => {
          if (currentValue) {
            return currentValue;
          }
          return profile?.line_of_business ?? availableOptions[0] ?? EMPTY_VALUE;
        });
      } catch {
        if (!isDisposed) {
          setLineOfBusinessOptionsError(LINE_OF_BUSINESS_OPTIONS_ERROR_MESSAGE);
        }
      } finally {
        if (!isDisposed) {
          setLineOfBusinessLoading(false);
        }
      }
    };

    void loadOptions();

    return () => {
      isDisposed = true;
    };
  }, [optionsRequestKey, profile?.line_of_business, shouldShowLineOfBusiness]);

  const handleConfirm = async (): Promise<void> => {
    const lineOfBusinessToSave = shouldShowLineOfBusiness
      ? selectedLineOfBusiness
      : null;

    if (!selectedPersona || (shouldShowLineOfBusiness && !lineOfBusinessToSave)) {
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const updatedProfile = await UserProfileService.updateProfileSelection({
        persona: selectedPersona,
        line_of_business: lineOfBusinessToSave,
      });
      setProfile(updatedProfile);
      // Continue to the knowledge-base selection step so the user can
      // pre-pick guideline documents that will be seeded into every new
      // project. That step is the one that finally navigates to '/'.
      navigate('/welcome/knowledge-base', { replace: true });
    } catch {
      setSaveError(SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="line-of-business-welcome">
      <div className="line-of-business-welcome__stage">
        <div className="line-of-business-welcome__card">
          <div
            className="line-of-business-welcome__hero"
            style={{
              backgroundImage: `url(${headerBg})`,
            }}
          >
            <div className="line-of-business-welcome__brand">
              <img src={logo} alt="Shell Logo" className="h-10 w-10" />
              <div>
                <span className="line-of-business-welcome__brandTitle">
                  {HERO_TITLE}
                </span>
              </div>
            </div>
          </div>

          <div className="line-of-business-welcome__body">
            <div className="line-of-business-welcome__intro">
              <h1 className="line-of-business-welcome__title">{WELCOME_TITLE}</h1>
              <p className="line-of-business-welcome__description">
                {WELCOME_DESCRIPTION}
              </p>
            </div>

            {(lineOfBusinessOptionsError || saveError) && (
              <div className="line-of-business-welcome__error" role="alert">
                <span>{saveError ?? lineOfBusinessOptionsError}</span>
                {lineOfBusinessOptionsError && shouldShowLineOfBusiness && !lineOfBusinessLoading && (
                  <button
                    type="button"
                    className="line-of-business-welcome__errorAction"
                    onClick={() => setOptionsRequestKey((currentValue) => currentValue + 1)}
                  >
                    {RETRY_OPTIONS_BUTTON_LABEL}
                  </button>
                )}
              </div>
            )}

            <section className="line-of-business-welcome__section">
              <div>
                <h2 className="line-of-business-welcome__sectionTitle">
                  {PERSONA_LABEL}
                </h2>
              </div>

              <div
                className="line-of-business-welcome__grid"
                aria-label={PERSONA_OPTIONS_ARIA_LABEL}
              >
                {PERSONA_OPTION_ITEMS.map((option) => {
                  const isSelected = option.value === selectedPersona;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`line-of-business-welcome__option${
                        isSelected ? ' is-selected' : ''
                      }${option.disabled ? ' is-disabled' : ''
                      }`}
                      onClick={() => {
                        if (option.disabled) {
                          return;
                        }
                        setSelectedPersona(option.value);
                        setSaveError(null);
                      }}
                      disabled={option.disabled}
                      aria-pressed={isSelected}
                    >
                      <span className="line-of-business-welcome__optionTitle">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {shouldShowLineOfBusiness && (
              <section className="line-of-business-welcome__section">
                <div>
                  <h2 className="line-of-business-welcome__sectionTitle">
                    {LINE_OF_BUSINESS_LABEL}
                  </h2>
                </div>

                {lineOfBusinessLoading ? (
                  <div className="line-of-business-welcome__loading">
                    <LoadingSpinner
                      label={LINE_OF_BUSINESS_OPTIONS_LOADING_LABEL}
                      size="lg"
                    />
                  </div>
                ) : (
                  <div
                    className="line-of-business-welcome__grid"
                    aria-label={LINE_OF_BUSINESS_OPTIONS_ARIA_LABEL}
                  >
                    {lineOfBusinessOptions.map((option) => {
                      const isSelected = option === selectedLineOfBusiness;

                      return (
                        <button
                          key={option}
                          type="button"
                          className={`line-of-business-welcome__option${
                            isSelected ? ' is-selected' : ''
                          }`}
                          onClick={() => {
                            setSelectedLineOfBusiness(option);
                            setSaveError(null);
                          }}
                          aria-pressed={isSelected}
                        >
                          <span className="line-of-business-welcome__optionTitle">
                            {option}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            <div className="line-of-business-welcome__actions">
              <button
                type="button"
                className="line-of-business-welcome__primaryAction"
                onClick={() => void handleConfirm()}
                disabled={isConfirmDisabled}
              >
                {saving ? SAVING_BUTTON_LABEL : SAVE_BUTTON_LABEL}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineOfBusinessWelcomePage;